import type { Nilable } from '~/utilities/type-guards';
import { isEmptyString, isNil, isNotNil, isString } from '~/utilities/type-guards';
import omit from 'lodash/omit';
import kebabCase from 'lodash/kebabCase';
import { colorHexWithHash } from '~/singlePageApp/components/DropdownColorPicker/colorUtils';
import { stringsAreEqual } from '~/utilities/strings';
import type {
  EventBrandValuesSet,
  EventRoomCustomizationsGeneratedFromBrand,
  RegistrationCustomizationsWithGeneratedValues,
  FormCustomizationsWithGeneratedValues,
  PageLevelCustomizations,
  PersistedPageLevelCustomizations,
  PageLevelCustomizationsInheritedFromBrand,
  PageToBrandValuesMap,
  GeneratedEventBrandValues,
  SavedEventBrandValues,
  PageToBrandValuesMapItem,
} from '../Brand/types';
import { generatedEventBrandValuesKeys } from '../Brand/types';

export const WHITE = 'ffffff';
export const WHITE_WITH_HEX = colorHexWithHash(WHITE);
export const DARK_COLOR = '37373c';
export const DARK_COLOR_WITH_HEX = colorHexWithHash(DARK_COLOR);
export const WISTIA_BLUE = '2949E5';

export const isBackgroundColorADefaultDarkBackgroundColorOption = (
  brand: EventBrandValuesSet,
  overrideBackgroundColor?: string,
): boolean => {
  const backgroundColor = overrideBackgroundColor ?? brand.backgroundColor;

  return (
    stringsAreEqual(backgroundColor, brand.brandDarkBackgroundColor, true) ||
    stringsAreEqual(backgroundColor, DARK_COLOR_WITH_HEX, true)
  );
};

export const isBackgroundColorADefaultLightBackgroundColorOption = (
  brand: EventBrandValuesSet,
  overrideBackgroundColor?: string,
): boolean => {
  const backgroundColor = overrideBackgroundColor ?? brand.backgroundColor;

  return (
    stringsAreEqual(backgroundColor, brand.brandLightBackgroundColor, true) ||
    stringsAreEqual(backgroundColor, WHITE_WITH_HEX, true)
  );
};

export const isBackgroundColorAnyDefaultBackgroundColorOption = (
  brand: EventBrandValuesSet,
  overrideBackgroundColor?: string,
): boolean => {
  return (
    isBackgroundColorADefaultDarkBackgroundColorOption(brand, overrideBackgroundColor) ||
    isBackgroundColorADefaultLightBackgroundColorOption(brand, overrideBackgroundColor)
  );
};

export const isBackgroundColorANonBrandedDefaultColor = (
  brand: EventBrandValuesSet,
  overrideBackgroundColor?: string,
): boolean => {
  const backgroundColor = overrideBackgroundColor ?? brand.backgroundColor;
  return (
    stringsAreEqual(backgroundColor, DARK_COLOR_WITH_HEX, true) ||
    stringsAreEqual(backgroundColor, WHITE_WITH_HEX, true)
  );
};

export const isBackgroundColorABrandedDefaultColor = (
  brand: EventBrandValuesSet,
  overrideBackgroundColor?: string,
): boolean => {
  const backgroundColor = overrideBackgroundColor ?? brand.backgroundColor;
  return (
    stringsAreEqual(backgroundColor, brand.brandLightBackgroundColor, true) ||
    stringsAreEqual(backgroundColor, brand.brandDarkBackgroundColor, true)
  );
};

export const mergeBrandValuesIntoPage = <T extends PageLevelCustomizations>(
  pageCustomizations: PersistedPageLevelCustomizations<T>,
  brand: GeneratedEventBrandValues,
  pageToBrandValues: PageToBrandValuesMap<T>,
): PageLevelCustomizationsInheritedFromBrand<T> => {
  const newCustomizations = { ...pageCustomizations };

  if (isNotNil(brand)) {
    Object.entries(brand).forEach(([brandKey, brandValue]) => {
      // If the page-level customizations don't yet have a value saved for this brand value,
      // apply the brand value
      if (isNil(newCustomizations[brandKey]) || isEmptyString(newCustomizations[brandKey])) {
        newCustomizations[brandKey] = brandValue;
      }
    });
  }

  // For each mapped value, check first to see if it has its own value.
  // If not, assign it to the mapped value from the registrationPage / brand.
  Object.entries(pageToBrandValues).forEach(
    ([keyInheritedFromBrand, { mappedBrandKey, alternateDefaultValue }]) => {
      const persistedPageCustomizationValue: unknown = pageCustomizations[keyInheritedFromBrand];
      const pageOrBrandMappedValue: unknown = newCustomizations[mappedBrandKey];

      // If there is no custom value for this property, we should either apply
      // the alternateDefaultValue if one exists or otherwise the mapped value set up in the PageToBrandValuesMap
      if (isNil(persistedPageCustomizationValue)) {
        newCustomizations[keyInheritedFromBrand] = isNotNil(alternateDefaultValue)
          ? alternateDefaultValue
          : pageOrBrandMappedValue;
      }

      // If the saved value is the name of a brand key, e.g., heroVideoLetterboxColor === 'slightAccentedBackgroundColor'
      // the previewed value for this customization should be the value for that brand key.
      if (persistedPageCustomizationValue === mappedBrandKey) {
        newCustomizations[keyInheritedFromBrand] = brand[
          mappedBrandKey
        ] as keyof EventBrandValuesSet;
      }
    },
  );

  return newCustomizations as PageLevelCustomizationsInheritedFromBrand<T>;
};

/**
 *
 * Given a full set of "previewed" page-level customizations, remove any value that either:
 *   1. has the same name and value as one of the brand customizations
 *   2. has the same value as the mapped brand key (e.g., sidebarBackgroundColor -> slightAccentedBackgroundColor)
 *
 * This will return a set of values for the page-level customizations that is fully distinct from the brand,
 * meaning that these values have all been configured by the user in one of the page-level menus.
 *
 * @param brand - the full set of event brand values, including generated values
 * @param pageCustomizations - the full set of page-level values, including generated values
 * @param pageToBrandValues - the map of page-level values that are supposed to inherit specific brand properties
 * @returns - a set of page-level customizations that does not contain any inherited brand values
 */
export const stripDefaultBrandValues = <T extends PageLevelCustomizations>(
  brand: EventBrandValuesSet,
  pageCustomizations: PageLevelCustomizationsInheritedFromBrand<T>,
  pageToBrandValues: PageToBrandValuesMap<T>,
): PersistedPageLevelCustomizations<T> => {
  const persistedCustomizations = {} as PersistedPageLevelCustomizations<T>;

  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const pageLevelKey in pageCustomizations) {
    if (Object.prototype.hasOwnProperty.call(pageCustomizations, pageLevelKey) === false) {
      break;
    }
    const safelyAccessibleKey = pageLevelKey as keyof EventBrandValuesSet;
    const pageLevelValue = pageCustomizations[pageLevelKey];
    const mappedBrandKey = isNotNil(pageToBrandValues[pageLevelKey])
      ? pageToBrandValues[pageLevelKey].mappedBrandKey
      : undefined;

    const mappedBrandValue = isNotNil(mappedBrandKey) ? brand[mappedBrandKey] : undefined;
    const brandValue = brand[safelyAccessibleKey];
    const brandValueOrMappedValue = isNotNil(mappedBrandKey) ? mappedBrandValue : brandValue;

    // If the page has its own value defined for this key and it doesn't match the default brand/mapped value, keep it
    if (isNotNil(pageLevelValue) && pageLevelValue !== brandValueOrMappedValue) {
      persistedCustomizations[safelyAccessibleKey] = pageLevelValue;
    }
  }

  const keysToOmit = [
    ...generatedEventBrandValuesKeys,
    'lightLogoBakeryHashedId',
    'darkLogoBakeryHashedId',
    'lightLogoUrl',
    'darkLogoUrl',
    '__typename',
  ];

  if (
    (pageCustomizations.isDarkMode &&
      isBackgroundColorADefaultDarkBackgroundColorOption(pageCustomizations)) ||
    (!pageCustomizations.isDarkMode &&
      isBackgroundColorADefaultLightBackgroundColorOption(pageCustomizations))
  ) {
    keysToOmit.push('backgroundColor');
  }

  return omit(persistedCustomizations, keysToOmit) as PersistedPageLevelCustomizations<T>;
};

export const getPersistedBrandCustomizations = (
  brand: EventBrandValuesSet,
): SavedEventBrandValues => {
  const savedBrandValues = { ...brand };

  const keysToOmit: string[] = [
    ...generatedEventBrandValuesKeys,
    'darkLogoUrl',
    'lightLogoUrl',
    '__typename',
  ];

  ['buttonBorderRadius', 'containerBorderRadius', 'inputBorderRadius'].forEach((radiusAttrName) => {
    const radiusAttr = savedBrandValues[radiusAttrName] as Nilable<number>;

    if (radiusAttr === savedBrandValues.borderRadius) {
      keysToOmit.push(radiusAttrName);
    }

    if (isNil(radiusAttr)) {
      savedBrandValues[radiusAttrName] = undefined;
    }
  });

  return omit(savedBrandValues, keysToOmit) as SavedEventBrandValues;
};

export const getPersistedPageLevelCustomizations = <T extends PageLevelCustomizations>(
  brand: EventBrandValuesSet,
  pageLevelCustomizations: PageLevelCustomizationsInheritedFromBrand<T>,
  pageToBrandValues: PageToBrandValuesMap<T>,
  omittedKeys: (keyof PageLevelCustomizationsInheritedFromBrand<T>)[],
): PersistedPageLevelCustomizations<T> => {
  const customizationsWithoutBrandDefaults = stripDefaultBrandValues<T>(
    brand,
    pageLevelCustomizations,
    pageToBrandValues,
  );

  const keysToOmit = [...omittedKeys];

  Object.entries(pageToBrandValues).forEach(
    ([keyInheritedFromBrand, mappedItem]: [
      keyInheritedFromBrand: keyof (EventBrandValuesSet & PageLevelCustomizations),
      mappedItem: PageToBrandValuesMapItem,
    ]) => {
      let pageLevelCustomizationValue: unknown = pageLevelCustomizations[keyInheritedFromBrand];
      let pageMappedValue: unknown = pageLevelCustomizations[mappedItem.mappedBrandKey];
      let brandMappedValue = brand[mappedItem.mappedBrandKey];
      // The alternate default value can be any value, not necessarily mapped to a brand value.
      // For example, the registration page heroVideoLetterboxColor has an alternateDefaultValue of '#000000'
      let alternateDefaultValue: unknown = isNotNil(mappedItem.alternateDefaultValue)
        ? mappedItem.alternateDefaultValue
        : null;

      // Make sure the equality check is case insensitive
      if (isString(pageLevelCustomizationValue)) {
        pageLevelCustomizationValue = pageLevelCustomizationValue.toLowerCase();
      }
      if (isString(pageMappedValue)) {
        pageMappedValue = pageMappedValue.toLowerCase();
      }
      if (isString(brandMappedValue)) {
        brandMappedValue = brandMappedValue.toLowerCase();
      }
      if (isString(alternateDefaultValue)) {
        alternateDefaultValue = alternateDefaultValue.toLowerCase();
      }

      // If the page-level customization value matches the mapped value (i.e., it does not have a custom value),
      // the persisted value should be the name of the brand key it's mapped to
      if (pageLevelCustomizationValue === brandMappedValue) {
        customizationsWithoutBrandDefaults[keyInheritedFromBrand] = mappedItem.mappedBrandKey;
      }

      /**
       *
       * First check to see if the page-level customization value matches the alternateDefaultValue for this customization key
       *
       * If no alternateDefaultValue exists for this customization key,
       * then we check to see if the customization value matches the value of the mapped key
       * from the page-level customizations if that key is defined on the page-level-customization,
       * otherwise we check to see if the customization value matches the value of the mapped key from the brand.
       *
       * If either of these cases are true, we do not save this key in the page-level customizations
       * since the value matches the default.
       *
       */
      const doesPageLevelCustomizationMatchAlternateDefaultValue =
        pageLevelCustomizationValue === alternateDefaultValue;
      const doesPagelLevelCustomizationMatchMappedValueWhenNoAlternateDefaultValueExists =
        isNil(mappedItem.alternateDefaultValue) &&
        pageLevelCustomizationValue === (pageMappedValue ?? brandMappedValue);
      if (
        doesPageLevelCustomizationMatchAlternateDefaultValue ||
        doesPagelLevelCustomizationMatchMappedValueWhenNoAlternateDefaultValueExists
      ) {
        keysToOmit.push(keyInheritedFromBrand);
      }
    },
  );

  return omit(
    customizationsWithoutBrandDefaults,
    keysToOmit,
  ) as PersistedPageLevelCustomizations<T>;
};

const userInputThemeValues = new Set([
  'customButtonText',
  'customButtonUrl',
  'customPostEventRedirectUrl',
  'logoTargetUrl',
  'logoUrl',
]);

export const getThemeValuesAsCssVars = (
  themeValues:
    | EventBrandValuesSet
    | EventRoomCustomizationsGeneratedFromBrand
    | FormCustomizationsWithGeneratedValues
    | RegistrationCustomizationsWithGeneratedValues,
  prefix: string,
): string => {
  const variables = Object.entries(themeValues)
    .filter(([key, value]) => {
      // ignore user input values which can break CSS due to unclosed starting characters such as " ( [
      return !userInputThemeValues.has(key) && value !== '';
    })
    .map(([key, value]) => {
      const variableName = `--${prefix}-${kebabCase(key)}`;
      if (isNil(value)) {
        return '';
      }
      if (variableName.endsWith('-url')) {
        return `${variableName}:url('${value}')`;
      }
      return `${variableName}:${value}`;
    })
    .join(';');
  return `* { ${variables} };`;
};
